import React from "react";
import { BoxProps } from "../Box";
import { Button } from "../Button";
import { WarningIcon } from "../Svg";
import { Text } from "../Text";
import { StateFrame } from "./styles";

export interface ErrorStateProps extends BoxProps {
  /** Headline, e.g. "Price data unavailable". */
  title: string;
  /** One line of plain-language cause, e.g. "The chart service did not answer." */
  description?: string;
  /** Manual retry (bounded automatic retry belongs to the caller). */
  onRetry?: () => void;
  retryLabel?: string;
  isRetrying?: boolean;
  /** "danger" for a failed fetch, "warning" for stale/partial data still shown. */
  tone?: "danger" | "warning";
  /** Optional secondary action (e.g. a docs link or a "use last known" button). */
  action?: React.ReactNode;
}

/**
 * Unavailable / error: explicit copy plus a retry, ring in the semantic colour and an icon
 * (never colour alone). Never a silent zero.
 */
export const ErrorState: React.FC<React.PropsWithChildren<ErrorStateProps>> = ({
  title,
  description,
  onRetry,
  retryLabel = "Retry",
  isRetrying = false,
  tone = "danger",
  action,
  children,
  ...props
}) => {
  const color = tone === "danger" ? "failure" : "warning";
  return (
    <StateFrame role="alert" $tone={tone} {...props}>
      <WarningIcon color={color} width="28px" mb="8px" aria-hidden />
      <Text bold color={color} fontSize="16px">
        {tone === "danger" ? "⚠ " : ""}
        {title}
      </Text>
      {description && (
        <Text fontSize="14px" color="textSubtle" mt="4px" maxWidth="420px">
          {description}
        </Text>
      )}
      {children}
      {(onRetry || action) && (
        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
          {onRetry && (
            <Button scale="sm" variant="secondary" onClick={onRetry} disabled={isRetrying} isLoading={isRetrying}>
              {retryLabel}
            </Button>
          )}
          {action}
        </div>
      )}
    </StateFrame>
  );
};

export default ErrorState;
